import { Link } from 'react-router-dom';
import { MapPin, Square, Tag, Eye, Building2, Store, ShoppingBag, Utensils, Laptop, ShoppingCart, Scissors, BookOpen, Dumbbell, Gem, Home, Shield, Camera, Briefcase, Shirt, ChefHat, Zap, Printer, Sparkles, Palette, Ruler, Bed } from 'lucide-react';

interface Shop {
  id?: string;
  _id?: string;
  shopNumber: string;
  size: number;
  purpose: string;
  floor: string;
  status: 'available' | 'reserved' | 'rented' | 'unavailable';
}

interface ShopCardProps {
  shop: Shop;
  viewMode: 'grid' | 'list';
}

const statusClass: Record<string, string> = {
  available: 'bg-primary-light text-primary',
  reserved: 'bg-amber-100 text-amber-800',
  rented: 'bg-stone-100 text-stone-700',
  unavailable: 'bg-red-100 text-red-800'
};

const iconMap: Record<string, JSX.Element> = {
  Pharmacy: <Building2 className="h-14 w-14 text-primary" />,
  'Clothing Store': <ShoppingBag className="h-14 w-14 text-primary" />,
  Confectionary: <Utensils className="h-14 w-14 text-primary" />,
  Electronics: <Laptop className="h-14 w-14 text-primary" />,
  Groceries: <ShoppingCart className="h-14 w-14 text-primary" />,
  'Beauty Salon': <Scissors className="h-14 w-14 text-primary" />,
  Bookstore: <BookOpen className="h-14 w-14 text-primary" />,
  'Sports Goods': <Dumbbell className="h-14 w-14 text-primary" />,
  Jewelry: <Gem className="h-14 w-14 text-primary" />,
  'Home & Garden': <Home className="h-14 w-14 text-primary" />,
  Office: <Building2 className="h-14 w-14 text-primary" />,
  'Gaming Hub': <Laptop className="h-14 w-14 text-primary" />,
  'Mens Wear': <ShoppingBag className="h-14 w-14 text-primary" />,
  'Womens Wear': <ShoppingBag className="h-14 w-14 text-primary" />,
  'Childrens Wear': <ShoppingBag className="h-14 w-14 text-primary" />,
  'Baby Wear': <ShoppingBag className="h-14 w-14 text-primary" />,
  Shoes: <ShoppingBag className="h-14 w-14 text-primary" />,
  Bags: <ShoppingBag className="h-14 w-14 text-primary" />,
  Accessories: <Gem className="h-14 w-14 text-primary" />,
  Watches: <Gem className="h-14 w-14 text-primary" />,
  PPE: <Shield className="h-14 w-14 text-primary" />,
  'Photo Studio': <Camera className="h-14 w-14 text-primary" />,
  'Hand Bags': <Briefcase className="h-14 w-14 text-primary" />,
  'Uniforms & Corporate Ware': <Shirt className="h-14 w-14 text-primary" />,
  Chinese: <ChefHat className="h-14 w-14 text-primary" />,
  'Electric Appliances': <Zap className="h-14 w-14 text-primary" />,
  Printing: <Printer className="h-14 w-14 text-primary" />,
  Perfumes: <Sparkles className="h-14 w-14 text-primary" />,
  Branding: <Palette className="h-14 w-14 text-primary" />,
  Tailor: <Ruler className="h-14 w-14 text-primary" />,
  Linen: <Bed className="h-14 w-14 text-primary" />,
  'Braids & Hair Products': <Scissors className="h-14 w-14 text-primary" />,
  'Deco & Homeware': <Home className="h-14 w-14 text-primary" />,
  Cosmetics: <Sparkles className="h-14 w-14 text-primary" />,
  Diapers: <Bed className="h-14 w-14 text-primary" />,
  Other: <Store className="h-14 w-14 text-primary" />
};

function getIcon(purpose: string) {
  return iconMap[purpose] ?? <Store className="h-14 w-14 text-primary" />;
}

export default function ShopCard({ shop, viewMode }: ShopCardProps) {
  const statusLabel = shop.status.charAt(0).toUpperCase() + shop.status.slice(1);
  const statusStyle = statusClass[shop.status] ?? statusClass.unavailable;

  if (viewMode === 'list') {
    return (
      <div className="rounded-2xl bg-surface-raised shadow-card overflow-hidden hover:shadow-card-hover transition-shadow">
        <div className="p-6 flex flex-col md:flex-row gap-6">
          <div className="md:w-40 shrink-0 flex items-center justify-center rounded-xl bg-surface-muted p-4">
            {getIcon(shop.purpose)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
              <h3 className="text-xl font-bold text-ink">Shop {shop.shopNumber}</h3>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyle}`}>
                {statusLabel}
              </span>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-ink-muted mb-4">
              <span className="flex items-center gap-1.5"><Square size={14} /> {shop.size.toFixed(1)} sqm</span>
              <span className="flex items-center gap-1.5"><MapPin size={14} /> {shop.floor}</span>
              <span className="flex items-center gap-1.5"><Tag size={14} /> {shop.purpose}</span>
            </div>
            <Link
              to={`/shops/${shop.id ?? shop._id}`}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-dark"
            >
              <Eye size={16} /> View
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-surface-raised shadow-card overflow-hidden hover:shadow-card-hover transition-shadow">
      <div className="bg-surface-muted p-8 flex items-center justify-center min-h-[160px] relative">
        {getIcon(shop.purpose)}
        <span className={`absolute top-3 right-3 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyle}`}>
          {statusLabel}
        </span>
      </div>
      <div className="p-5">
        <h3 className="text-lg font-bold text-ink mb-3">Shop {shop.shopNumber}</h3>
        <div className="space-y-1.5 text-sm text-ink-muted mb-4">
          <div className="flex items-center gap-2"><Square size={14} /> {shop.size.toFixed(1)} sqm</div>
          <div className="flex items-center gap-2"><MapPin size={14} /> {shop.floor}</div>
          <div className="flex items-center gap-2"><Tag size={14} /> {shop.purpose}</div>
        </div>
        <Link
          to={`/shops/${shop.id ?? shop._id}`}
          className="block w-full py-2.5 rounded-xl bg-primary text-white text-center font-semibold hover:bg-primary-dark"
        >
          View & Apply
        </Link>
      </div>
    </div>
  );
}
